import { createContext, ReactNode, useContext, useMemo } from 'react'

import { EntitySchemaItem } from 'api/ApiTypes'
import { getCustomerApiClient } from 'api/CustomerApiClient'
import { CUSTOMER_QUERY_IDS } from 'api/Queries'
import { useQueryWithMock } from 'dummy/static'
import CommonUtils from 'utils/CommonUtils'

import { EntityType } from '../utils/EntityUtils'

import AuthContext from './AuthContext'
import PortalConfigContext, {
  PortalConfigContextState
} from './PortalConfigContext'
interface SchemasContextState {
  isLoading: boolean
  schemas: EntitySchemaItem[]
}

const defaultState: SchemasContextState = {
  isLoading: true,
  schemas: []
}

const SchemasContext = createContext<SchemasContextState>(defaultState)

interface SchemasContextProps {
  children: ReactNode
}

const SchemasContextProvider = (props: SchemasContextProps) => {
  const { data: authData } = useContext(AuthContext)
  const { data: portalConfigData } =
    useContext<PortalConfigContextState>(PortalConfigContext)

  const orgId = authData?.user?.attributes?.['custom:org_id']
  const token = portalConfigData.portalConfig.accessToken

  const schemasQuery: any = useQueryWithMock({
    queryKey: [
      CUSTOMER_QUERY_IDS.GET_SCHEMAS,
      authData?.authenticated.toString(),
      orgId
    ],
    queryFn: () =>
      authData.authenticated && orgId
        ? getCustomerApiClient().getSchemas()
        : getCustomerApiClient().getSchemasByDomain(
            {
              domain: CommonUtils.getEcpDomain()
            },
            null,
            { headers: { authorization: `Bearer ${token}` } }
          ),
    enabled: authData.initialized
  })

  const schemas = useMemo(() => {
    return schemasQuery?.data?.schemas || []
  }, [schemasQuery?.data?.schemas])

  return (
    <SchemasContext.Provider
      value={{ schemas, isLoading: schemasQuery.isLoading }}
    >
      {props.children}
    </SchemasContext.Provider>
  )
}

const useContextSchema = (schemaSlug: EntityType) => {
  const { schemas, isLoading } = useContext(SchemasContext)

  const schema = schemas.find(
    (schema: EntitySchemaItem) => schema.slug === schemaSlug
  )

  return { schema, isLoading }
}

const useContextSchemas = () => {
  const { schemas, isLoading } = useContext(SchemasContext)

  return { schemas, isLoading }
}

export { SchemasContextProvider, useContextSchema, useContextSchemas }
export default SchemasContext
